import { SpeedRounded } from '@mui/icons-material'
import { Box, Button, ButtonGroup, Stack, Typography, useTheme } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useAppSelector } from '../../store/Hooks'
import { overwriteRouteParams } from '../../store/slices/SessionSlice'
import CostUtilizationView from '../contract_analytics/CostUtilizationView'
import ExpensesByTosView from '../contract_analytics/ExpensesByTosView'
import NormalView from '../contract_analytics/NormalView'
import HelixLoader from '../loaders/HelixLoader'
import { DataViewMode, IMenuItem, IRouteParameters } from '../routes/ContractDetails'
import RoutingManager from '../routing/Routing'
import { snackbar } from '../services/SnackbarService'

interface P {
    visualizedFilterDate: string
    updateDataFlag: boolean
    handleFilterDateChange: (newValue: moment.Moment | null) => void
    handleCsvGeneration: (fileType: string, source?: string) => void
    setExportMenu: (items: IMenuItem[]) => void
    viewMode?: DataViewMode
    setViewMode?: (name: DataViewMode) => void
}

export type SectionView = 'main' | 'expenseByTos' | 'costUtilization'

export default function NewContractAnalytics({ visualizedFilterDate, updateDataFlag, handleFilterDateChange, handleCsvGeneration, setExportMenu }: P) {
    const theme = useTheme()
    const dispatch = useDispatch()
    const params = RoutingManager.getParams<IRouteParameters>(useAppSelector(s => s.session.routeParams))
    const [activeView, setActiveView] = useState<SectionView>(params?.analyticsSection ? params.analyticsSection : 'main')

    const underDev = () => {
        snackbar.show({
            severity: 'info',
            text: 'This functionality will be developed in future.'
        })
    }

    const currentView = (view: SectionView) => {
        switch (view) {
            case 'main':
                return (
                    <NormalView
                        contractId={params.contractId}
                        changeView={
                            (view: SectionView) => {
                                setActiveView(view)
                                dispatch(overwriteRouteParams({ ...params, analyticsSection: 'main' }))
                            }
                        }
                        filterEndDate={params.endDate}
                        filterStartDate={params.startDate}
                        displayUnderDev={underDev}
                        updateDataFlag={updateDataFlag}
                        handleCsvGeneration={handleCsvGeneration}
                        setExportMenu={setExportMenu}
                        viewMode={params.dataViewMode}
                    />
                )
            case 'expenseByTos':
                return (
                    <ExpensesByTosView
                        updateDataFlag={updateDataFlag}
                        setExportMenu={setExportMenu}
                        changeView={
                            (view: SectionView) => {
                                setActiveView(view)
                                dispatch(overwriteRouteParams({ ...params, analyticsSection: 'expenseByTos' }))
                            }
                        }
                    />
                )
            case 'costUtilization':
                return (
                    <CostUtilizationView
                        contractId={params.contractId}
                        filterEndDate={params.endDate}
                        filterStartDate={params.startDate}
                        updateDataFlag={updateDataFlag}
                        setExportMenu={setExportMenu}
                        changeView={
                            (view: SectionView) => {
                                setActiveView(view)
                                dispatch(overwriteRouteParams({ ...params, analyticsSection: 'costUtilization' }))
                            }
                        }
                    />
                )
            default:
                <Stack justifyContent='center' alignItems='center' height='calc(100vh - 87px)'>
                    <HelixLoader />
                </Stack>
                break
        }
    }

    return (
        <Stack sx={{ height: '100%' }}>
            <Box>
                <Stack sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 }}>
                    <Stack sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 2, alignItems: 'center' }}>
                        <ButtonGroup>
                            <Button variant='contained' color='info' sx={{ textTransform: 'capitalize' }} onClick={underDev}>Performance</Button>
                            <Button variant='outlined' sx={{ textTransform: 'capitalize' }} onClick={underDev}>Operations</Button>
                        </ButtonGroup>
                        <Button variant='text' sx={{ textTransform: 'capitalize' }} startIcon={<SpeedRounded />} onClick={underDev}>Quality performance</Button>
                    </Stack>
                    <ButtonGroup
                        sx={{
                            'display': 'flex',
                            'alignItems': 'stretch',
                            '& .MuiButtonGroup-grouped': {
                                minWidth: 'fit-content'
                            }
                        }}>

                        <Box sx={{
                            display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: '20px 0 0 20px !important', border: '1px solid', px: 2
                        }}>
                            <Typography variant='button' color={theme.palette.text.secondary} sx={{ textTransform: 'capitalize' }}>{visualizedFilterDate}</Typography>
                        </Box>

                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                views={['month', 'year']}
                                onAccept={handleFilterDateChange}
                                value={moment(params.endDate, 'YYYY-MM')}
                                maxDate={moment()}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        sx: {
                                            'fontSize': '0.875rem',
                                            '& .MuiOutlinedInput-root': {
                                                'borderRadius': '0 20px 20px 0 !important',
                                                '& fieldset': {
                                                    border: '1px solid',
                                                    borderLeft: 'none'
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderWidth: '1px'
                                                }
                                            },
                                            '& .MuiInputBase-input': {
                                                padding: '8px 12px',
                                                minWidth: '120px',
                                                width: 'auto'
                                            },
                                            '& .MuiInputAdornment-root': {
                                                marginLeft: 0
                                            }
                                        }
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </ButtonGroup>
                </Stack>
                {currentView(activeView)}
            </Box>
        </Stack>
    )
}
